/** Direct email-only Klaviyo signup. Only public IDs are exposed; no Shopify fallback. */
function connectGiveawaySignup(form, lifecycle) {
  if (!form) return;
  const controls = [...form.querySelectorAll('input, button[type="submit"]')];
  const status = form.querySelector('[role="status"]');
  const company = form.dataset.klaviyoCompany?.trim();
  const list = form.dataset.klaviyoList?.trim();
  const preview = form.dataset.preview === 'true';
  const configured = /^[a-zA-Z0-9]{6}$/.test(company || '') && /^[a-zA-Z0-9]{6}$/.test(list || '');
  let busy = false;
  let completed = false;
  const disable = (value) => controls.forEach(control => { control.disabled = value; });
  const announce = (message) => {
    status.hidden = false;
    status.textContent = message;
  };
  // Always prevent native navigation, including missing/invalid configuration.
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (busy || completed || lifecycle.aborted) return;
    if (!preview && !configured) {
      announce('Signup is temporarily unavailable. Your details have not been sent. Please try again later.');
      return;
    }
    if (!form.reportValidity()) return;
    const firstName = form.querySelector('[name="first_name"]').value.trim();
    const email = form.querySelector('[name="email"]').value.trim();
    const preference = form.querySelector('[name="shopping_preference"]:checked')?.value;
    if (!firstName || !['women', 'men', 'both'].includes(preference)) {
      announce('Please enter your first name and choose your shopping preference.');
      return;
    }
    if (preview) {
      announce('Local preview: your details pass validation. Nothing has been sent and no entry has been created.');
      return;
    }
    busy = true;
    disable(true);
    form.setAttribute('aria-busy', 'true');
    announce('Sending your signup…');
    const request = new AbortController();
    const abort = () => request.abort();
    lifecycle.addEventListener('abort', abort, { once: true });
    const timeout = setTimeout(abort, 10000);
    try {
      const response = await fetch(`https://a.klaviyo.com/client/subscriptions?${new URLSearchParams({ company_id: company })}`, {
        method: 'POST',
        headers: { accept: 'application/vnd.api+json', 'content-type': 'application/vnd.api+json', revision: '2026-07-15' },
        body: JSON.stringify({ data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Black Friday 2026 Giveaway',
            profile: { data: { type: 'profile', attributes: { email, first_name: firstName, properties: {
              giveaway_campaign: 'black-friday-2026-giveaway', giveaway_shopping_preference: preference,
            } } } },
          },
          relationships: { list: { data: { type: 'list', id: list } } },
        } }),
        signal: request.signal,
      });
      if (lifecycle.aborted) return;
      if (!response.ok) throw new Error('Signup not accepted');
      completed = true;
      announce('Signup request received. Check your inbox for any confirmation email to complete your subscription.');
    } catch {
      if (!lifecycle.aborted) announce('We could not confirm your signup. Please try again.');
    } finally {
      clearTimeout(timeout);
      lifecycle.removeEventListener('abort', abort);
      busy = false;
      form.removeAttribute('aria-busy');
      disable(completed || lifecycle.aborted);
    }
  }, { signal: lifecycle });
  lifecycle.addEventListener('abort', () => disable(true), { once: true });
  disable(!preview && !configured);
  if (!preview && !configured) announce('Signup is temporarily unavailable. Your details have not been sent. Please try again later.');
}

/** Countdown and native FAQ enhancement; runs identically in the guarded local preview. */
class EchtGiveaway extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;

    connectGiveawaySignup(this.querySelector('form'), signal);

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !(event.target instanceof Element)) return;
      const details = event.target.closest('details[open]');
      if (!details) return;
      details.open = false;
      details.querySelector('summary')?.focus();
    }, { signal });

    clearInterval(this.timer);
    const target = Date.parse(this.dataset.saleDate || '');
    const countdown = this.querySelector('[data-countdown]');
    if (!Number.isFinite(target) || !countdown) return;
    const timer = countdown.querySelector('[role="timer"]');
    const live = this.querySelector('[data-live]');
    const update = () => {
      const remaining = Math.max(0, Math.floor((target - Date.now()) / 1000));
      if (remaining === 0) {
        countdown.hidden = true;
        if (live) live.hidden = false;
        clearInterval(this.timer);
        return;
      }
      const units = { days: Math.floor(remaining / 86400), hours: Math.floor(remaining / 3600) % 24, minutes: Math.floor(remaining / 60) % 60, seconds: remaining % 60 };
      for (const [unit, value] of Object.entries(units)) {
        const cell = countdown.querySelector(`[data-unit="${unit}"]`);
        if (cell) cell.textContent = String(value).padStart(2, '0');
      }
      timer?.setAttribute('aria-label', `${units.days} days, ${units.hours} hours, ${units.minutes} minutes, ${units.seconds} seconds until sale opens`);
    };
    update();
    if (!countdown.hidden) this.timer = setInterval(update, 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    this.controller?.abort();
  }
}
if (!customElements.get('echt-giveaway')) customElements.define('echt-giveaway', EchtGiveaway);
